import React from 'react';
import { Card } from '@mui/material';

import { CoverageSummaryResolver } from '../../workflows/policies/midterm-endorsements/components/CoverageSummaryResolver';
import { MTESummary } from './PxMidTermEndorsementDrawer/MTESummary';
import { MTEHeader } from './PxMidTermEndorsementDrawer/MTEHeader';
import { InsuredInformationSummary } from '../../workflows/policies/midterm-endorsements/screens/EndorsementSummary/InsuredInformationSummary';

import {
  composeEndorsementCoverageInfo,
  composeEndorsementInsuredInfo,
  determineIfInsuredInformationDelta,
} from './PxMidTermEndorsementDrawer/helpers/midtern-endorsement-drawer.utils';
import { Show } from '../../../components/Show';
import { usePolicyDetails } from '../../../components/hooks/queries/policies/usePolicyDetails';

interface PxMidTermEndorsementDrawerProps {
  endorsement: any;
}

export const PxMidTermEndorsementDrawer = ({
  endorsement,
}: PxMidTermEndorsementDrawerProps) => {
  const policyDetailsQuery = usePolicyDetails({
    id: endorsement?.prevPolicyIdInChain,
    product: endorsement?.product,
  });

  const transformedDetailsData = React.useMemo(() => {
    if (policyDetailsQuery.data) {
      const { data } = policyDetailsQuery;

      return {
        ...data,
        policy: {
          ...data.policy,
          firmographicData: {
            ...data.firmographicData,
          },
          aggregateLimit: data.policy.limit,
          productType: data.policy.product,
        },
      };
    }

    return undefined;
  }, [policyDetailsQuery]);

  const endorsementCoverageInfo = composeEndorsementCoverageInfo({
    endorsement,
  });

  const endorsementInsuredInfo = composeEndorsementInsuredInfo({
    endorsement,
  });

  if (policyDetailsQuery.isLoading) {
    return null;
  }

  const hasInsuredInformationDelta = determineIfInsuredInformationDelta(
    endorsementInsuredInfo,
    {
      ...transformedDetailsData?.firmographicData,
      companyName: transformedDetailsData?.policy.companyName,
    }
  );

  return (
    <>
      <MTEHeader
        title={endorsement?.quoteNumber}
        subtitle={endorsement?.companyName}
      />

      <MTESummary
        endorsement={endorsement}
        currency={policyDetailsQuery.data?.currency}
      />

      <Show when={hasInsuredInformationDelta}>
        <Card sx={{ marginBottom: '1rem' }}>
          <InsuredInformationSummary
            endorsementDetails={endorsementInsuredInfo}
            policyDetails={{
              ...transformedDetailsData?.firmographicData,
              companyName: transformedDetailsData?.policy.companyName,
            }}
          />
        </Card>
      </Show>

      <CoverageSummaryResolver
        progressData={endorsementCoverageInfo}
        policyDetails={transformedDetailsData?.policy}
        endorsementStartDate={endorsement?.effectiveDate}
      />
    </>
  );
};
